import type { ReactNode } from "react";
import "./index.css";
import { clsx } from "clsx";
import { getInitials } from "../../utils/getInitials";
import { Skeleton } from "../Skeleton";
import { fontClassMap } from "./constants";
import type { IAvatarProps } from "./types";

export const Avatar = ({
  size = "sm",
  shape = "rounded",
  color = "neutral",
  text,
  subtext,
  initials,
  src,
  alt,
  showDetails = true,
  className,
  loading,
  direction = "default",
  placeholderIcon,
  ref,
  ...htmlProps
}: IAvatarProps) => {
  const rootClass = clsx(
    "nuco-avatar",
    `nuco-avatar--${size}`,
    `nuco-avatar--${shape}`,
    `nuco-avatar--${direction}`,
    className,
  );

  const letters: string = initials || getInitials(text);

  const imgJSX = loading ? (
    <Skeleton
      width={fontClassMap[size].skeletonImageSize}
      height={fontClassMap[size].skeletonImageSize}
      shape={shape}
    />
  ) : (
    <div className={clsx("nuco-avatar__circle", `nuco-color-${color}`)}>
      {src && <img className="nuco-avatar__image" alt={alt} src={src} />}
      {!src && placeholderIcon && (
        <span className="nuco-avatar__icon">{placeholderIcon}</span>
      )}
      {!src && !placeholderIcon && letters && (
        <span className="nuco-avatar__letters">{letters}</span>
      )}
    </div>
  );

  const titleJSX: ReactNode =
    text &&
    (loading ? (
      <Skeleton
        width={fontClassMap[size].skeletonTitleWidth}
        height={fontClassMap[size].skeletonTitleHeight}
      />
    ) : (
      <div className={`nuco-avatar__text ${fontClassMap[size].title}`}>
        {text}
      </div>
    ));

  const subTextJSX: ReactNode =
    subtext &&
    (loading ? (
      <Skeleton
        width={fontClassMap[size].skeletonSubtextWidth}
        height={fontClassMap[size].skeletonSubtextHeight}
      />
    ) : (
      <div className={`nuco-avatar__subtext ${fontClassMap[size].subtext}`}>
        {subtext}
      </div>
    ));

  return (
    <div {...htmlProps} ref={ref} className={rootClass}>
      {imgJSX}
      {showDetails && (text || subtext) && (
        <div className="nuco-avatar__details">
          {titleJSX}
          {subTextJSX}
        </div>
      )}
    </div>
  );
};
